<template>
    <SpinnerLoading v-if="loading" class="mt-50" />
    <template v-else>
        <form v-on:submit="submit">
            <div class="row schedule-therapy-dates-box">
                <div class="col-sm-6">
                    <!-- DATE RANGE -->
                    <div class="recent-activity-box">
                        <ul class="list ps-0 mb-0 list-unstyled">
                            <li class="position-relative fw-medium text-dark-emphasis pt-10 pe-0 ps-50">
                                <div class="icon position-absolute start-0 rounded-circle text-center">
                                    <i class="ph-duotone ph-play"></i>
                                </div>
                                <!-- START AT -->
                                <div class="form-group">
                                    <label class="d-block text-black fw-semibold mb-10">
                                        {{ $t('form.start_at.label') }}
                                    </label>
                                    <input v-model="range.start" type="date" class="form-control shadow-none text-black"
                                        required :class="[{ 'is-invalid': !range.start || errors?.start_at }]"
                                        :min="YYYYMMDD(_range.start.toDate())" />
                                </div>
                                <!-- ERRORS -->
                                <div class="form-messages mt-2 px-1" v-if="errors?.start_at ?? false">
                                    <p v-for="error in errors.start_at" :key="error"
                                        class="small text-danger lh-1 m-0 mt-1">{{ error }}</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col-sm-6">
                    <!-- DATE RANGE -->
                    <div class="recent-activity-box">
                        <ul class="list ps-0 mb-0 list-unstyled">
                            <li class="position-relative fw-medium text-dark-emphasis pt-10 pb-20 pe-0 ps-50"
                                :class="[showError ? 'danger-step' : 'success-step']">
                                <div class="icon position-absolute start-0 rounded-circle text-center">
                                    <i class="ph-duotone"
                                        :class="[showError ? 'ph-warning-circle' : 'ph-check-circle']"></i>
                                </div>
                                <!-- START AT -->
                                <div class="form-group">
                                    <label class="d-block text-black fw-semibold mb-10">
                                        {{ $t('form.end_at.label') }}
                                    </label>
                                    <input v-model="range.end" type="date" class="form-control shadow-none text-black"
                                        required :min="YYYYMMDD(_range.start.toDate())"
                                        :class="[{ 'is-invalid': showError || errors?.stop_at }]" />
                                    <!-- ERRORS -->
                                    <div class="form-messages mt-2 px-1" v-if="showError">
                                        <p class="small text-danger lh-1 m-0 mt-1">{{
                                            $t('form.end_at.$error.therapy_range_error', { min: _range.min }) }}</p>
                                    </div>
                                    <div class="form-messages mt-2 px-1" v-if="errors?.stop_at ?? false">
                                        <p v-for="error in errors.stop_at" :key="error"
                                            class="small text-danger lh-1 m-0 mt-1">{{ error }}</p>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="row" v-if="therapy_schemes && therapy_schemes?.length > 1">
                <div class="col-12">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                        <label class="d-block text-black fw-semibold mb-10">
                            {{ $t('feature.therapy.drug') }}
                        </label>
                        <div class="d-flex gap-3 flex-wrap">
                            <div class="d-flex">
                                <select class="form-select shadow-none text-black fs-md-15 pe-50"
                                    v-model="selected_scheme_id">
                                    <!-- OPTION WITHOUT CLASS NAME -->
                                    <template v-for="(therapy_schemes, group_name) in scheme_groups" :key="group_name">
                                        <template v-if="group_name === 'undefined'">
                                            <option v-for="scheme in therapy_schemes" :key="scheme.id"
                                                :value="scheme.id">
                                                {{ scheme.drug }}
                                                <template v-if="getSchemeRule(scheme)?.intake_per_day !== undefined">
                                                    - {{ $tc('feature.therapy.intakes_per_day',
                                                        getSchemeRule(scheme)!.intake_per_day) }}{{ getSchemeDose(scheme) }}
                                                </template>
                                            </option>
                                        </template>
                                        <!-- OPTION GROUPS WITH CLASS NAME -->
                                        <template v-else>
                                            <optgroup :label="`${group_name}`">
                                                <option v-for="scheme in therapy_schemes" :key="scheme.id"
                                                    :value="scheme.id">
                                                    {{ scheme.drug }}
                                                    <template
                                                        v-if="getSchemeRule(scheme)?.intake_per_day !== undefined">
                                                        - {{ $tc('feature.therapy.intakes_per_day',
                                                            getSchemeRule(scheme)!.intake_per_day) }}{{ getSchemeDose(scheme) }}
                                                    </template>
                                                </option>
                                            </optgroup>
                                        </template>
                                    </template>
                                </select>
                            </div>
                            <div class="d-flex align-items-end justify-content-end gap-5">
                                <button class="default-btn outlined transition fw-medium fs-md-15 fs-lg-16"
                                    :disabled="!selected_scheme_id" v-on:click="addScheme" type="button">
                                    {{ $t('cta.add') }}
                                </button>
                                <button v-if="show_add_PRN" type="button"
                                    class="default-btn outlined transition fw-medium fs-md-15 fs-lg-16"
                                    v-on:click="addAllPRN">
                                    {{ $t('feature.therapy.add_all_prn') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="table-responsive mb-3" v-if="schemes.length != 0">
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                {{ $t('feature.therapy.drug') }}
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                {{ $t('feature.therapy.dose') }}
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
                                v-if="config.feature.prn_therapies">
                                {{ $t('feature.therapy.prn') }}
                            </th>
                            <th scope="col" v-if="therapy_schemes?.length !== 1"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <!-- CENTER ROW -->
                        <tr v-for="scheme in schemes" :key="scheme.id">
                            <td class="shadow-none lh-1 fw-medium text-primary">
                                {{ scheme.drug }}
                                <template v-if="getSchemeRule(scheme)?.intake_per_day !== undefined">
                                    -
                                    {{ $tc('feature.therapy.intakes_per_day',
                                        getSchemeRule(scheme)!.intake_per_day) }}
                                </template>
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                <select class="form-select shadow-none text-black fs-md-15 w-auto"
                                    v-if="show_scheme_doses(scheme)" v-model="scheme.default_dose" required>
                                    <option v-for="dose in scheme.doses" :key="dose" :value="dose">
                                        {{ dose }}
                                    </option>
                                </select>
                                <div v-else>
                                    {{ scheme.default_dose }}
                                </div>
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis"
                                v-if="config.feature.prn_therapies">
                                <div class="form-check form-switch d-inline-block">
                                    <input class="form-check-input shadow-none" type="checkbox" role="switch" disabled
                                        v-model="scheme.is_prn">
                                </div>
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end"
                                v-if="therapy_schemes?.length !== 1">
                                <button class="btn-close" type="button" @click="removeScheme(scheme)">
                                    <i class="ph-bold ph-x"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- ACTION -->
            <div class="d-flex align-items-end justify-content-end flex-1 ms-auto gap-3">
                <button class="default-btn outlined transition fw-medium fs-md-15 fs-lg-16" v-on:click="discard"
                    type="button">
                    {{ $t('cta.discard') }}
                </button>
                <button type="submit" class="default-btn transition fw-medium fs-md-15 fs-lg-16"
                    :disabled="showError || schemes.length == 0">
                    {{ $t('cta.schedule_therapy') }}
                </button>
            </div>
        </form>
    </template>
</template>

<style lang="scss">
.schedule-therapy-dates-box {
    row-gap: 1rem;
}
</style>

<script lang="ts">
import DateFormatUtils from '@/utils/methods/date_formats';
import { PatientInfo } from '@/utils/models/user.model';
import API from '@/services/api/api.service';
import dayjs from 'dayjs';
import { PropType } from 'vue';
import { defineComponent } from 'vue';
import SpinnerLoading from "@/components/Widgets/Spinner.vue"
import { Therapy, TherapyScheme, TherapySchemeRule } from '@/utils/models/therapy.model';
import config from '@/utils/const/config';
import { removeNullProperties } from '@/utils/methods/remove_null_properties';
import deepMerge from '@/utils/methods/deep_merge';

export default defineComponent({
    name: "ScheduleTherapy",
    props: {
        patient: {
            type: Object as PropType<PatientInfo>,
            required: true
        }
    },
    data: function () {
        return {
            selected_scheme_id: undefined as string | undefined,
            schemes: [] as TherapyScheme[],
            today: dayjs().toDate(),
            date: dayjs().toDate(),
            range: {
                start: DateFormatUtils.YYYYMMDD(dayjs().toDate()),
                end: DateFormatUtils.YYYYMMDD(dayjs().add(31, 'day').toDate()),
            },
            errors: undefined as any,
            loading: false,
            therapy_schemes: undefined as TherapyScheme[] | undefined,
        }
    },
    computed: {
        _range() {
            let minDurations: number[] = [];
            this.schemes.forEach((scheme) => {
                if (scheme.min_duration != undefined) {
                    minDurations.push(
                        parseInt(scheme!.min_duration!)
                    );
                }
            });
            let min = Math.max(...minDurations) ?? undefined;
            return {
                start: dayjs(this.range.start),
                end: min != Infinity ? dayjs(this.range.start).add(min, 'day') : dayjs(this.range.start),
                min: min
            }
        },
        scheme_groups(): { [key: string | 'undefined']: TherapyScheme[] } {
            return ((this.therapy_schemes ?? []) as TherapyScheme[]).sort((a, b) => (a.drug!.toLowerCase()).localeCompare(b.drug!.toLowerCase())).map((e) => removeNullProperties<TherapyScheme>(e)).sort((a, b) => (a.class?.toLowerCase() ?? 'ZZZ').localeCompare(b.class?.toLowerCase() ?? 'ZZZ')).reduce((acc, cur) => {
                const class_name = cur.class ?? 'undefined';
                return { ...acc, [class_name]: [...(acc[class_name] || []), cur] };
            }, {} as { [key: string | 'undefined']: TherapyScheme[] });
        },
        showError() {
            return dayjs(this.range.end) < this._range.end;
        },
        attributes() {
            var range = {
                highlight: {
                    start: { fillMode: 'solid' },
                    base: { fillMode: 'light', color: 'red' },
                    end: { fillMode: 'light', color: 'red' },
                },
                dates: this._range,
            };
            return this.showError ? [range] : [];
        },
        YYYYMMDD() {
            return DateFormatUtils.YYYYMMDD;
        },
        config() {
            return config;
        },
        show_doses(): boolean {
            return this.schemes.some((scheme) => scheme.doses !== undefined && scheme.doses !== null && scheme.doses.length !== 0);
        },
        show_add_PRN(): boolean {
            return this.therapy_schemes?.some((scheme) => scheme.is_prn == true) ?? false;
        }
    },
    mounted() {
        this.loadSchemes();
    },
    methods: {
        submit(event): void {
            event.preventDefault();

            this.scheduleTherapy();
        },
        loadSchemes(page?: number) {
            API.getTherapySchemes(page).then((response) => {
                this.therapy_schemes ??= [];
                this.therapy_schemes.push(...response.data.data);
                if (this.therapy_schemes?.length === 1) {
                    this.schemes.push(...this.therapy_schemes);
                    this.range.end = DateFormatUtils.YYYYMMDD(this._range.end.toDate());
                }
                if (response.data.current_page !== response.data.last_page) {
                    this.loadSchemes(response.data.current_page + 1);
                }
            });
        },
        provideTherapy() {
            this.loadSchemes();
            this.range = {
                start: DateFormatUtils.YYYYMMDD(dayjs().toDate()),
                end: DateFormatUtils.YYYYMMDD(dayjs().add(31, 'day').toDate())
            };
        },
        scheduleTherapy() {
            console.log(this.schemes);
            this.loading = true;
            this.errors = undefined;
            Promise.allSettled(this.schemes.map((scheme) => API.scheduleTherapy({ patient_id: this.patient.id!, start: dayjs(this.range.start).toDate(), end: dayjs(this.range.end).toDate(), therapy_scheme_id: scheme.id, is_prn: scheme.is_prn == true, dosage: (scheme.doses !== undefined && scheme.doses !== null) ? scheme.default_dose : undefined }))).then((responses) => {
                let success = responses.filter((response) => response.status === 'fulfilled' && response.value.data.success === true).map((res) => (res as PromiseFulfilledResult<any>).value.data.data as Therapy)
                let failed = responses.filter((response) => response.status === 'rejected' || response.value.data.success === false);
                // EMIT SCHEDULED THERAPIES 
                // to add them in therapy list
                // failed boolean value to check if close schedule therapy box or not
                if (success.length !== 0) {
                    this.$emit('scheduled', {
                        success: success,
                        failed: failed.length !== 0,
                    } as ScheduleTherapies);
                }

                // remove scheduled therapies from the 'queue'
                const success_ids = success.map((therapy) => therapy.scheme!.id!);
                this.schemes = this.schemes.filter((scheme) => !success_ids.includes(scheme.id));

                // SET ERRORS
                this.errors = {};
                failed.forEach((el) => {
                    const errors = (el as PromiseRejectedResult).reason.response?.data?.errors;
                    this.errors = deepMerge(this.errors, errors);
                });

            }).finally(() => {
                this.loading = false;
            }).catch((error) => {
                this.errors = error.response?.data?.errors;
            });
        },
        discard() {
            this.$emit('discard');
        },
        addScheme() {
            let _scheme = { ...(this.therapy_schemes?.find((e) => e.id == this.selected_scheme_id) as TherapyScheme) };
            if (!_scheme || this.schemes.find((e) => e.id == this.selected_scheme_id) != undefined) return;
            this.schemes.unshift(_scheme);
            this.selected_scheme_id = undefined;
        },
        addAllPRN() {
            this.schemes = this.schemes.filter((scheme) => scheme.is_prn != true);
            const PRN = this.therapy_schemes?.filter((e) => e.is_prn == true) as TherapyScheme[];
            this.schemes.push(...PRN);
        },
        removeScheme(scheme: TherapyScheme) {
            let index = this.schemes.indexOf(scheme);
            if (index >= 0) {
                this.schemes.splice(index, 1);
            }
        },
        show_scheme_doses(scheme: TherapyScheme): boolean {
            return scheme.doses !== undefined && scheme.doses !== null && scheme.doses.length !== 0;
        },
        getSchemeRule(scheme: TherapyScheme): TherapySchemeRule | undefined {
            return [...(scheme.rules ?? [])].shift();
        },
        getSchemeDose(scheme: TherapyScheme): string {
            let dose: string | undefined;
            if((scheme.default_dose ?? '').length == 0) {
                dose = `${[...scheme.doses ?? []].shift()} / ${[...scheme.doses ?? []].pop()}`
            } else {
                dose = scheme.default_dose;
            }
            return dose == undefined ? '' : ` - ${dose}`;
        }
    },
    emits: ['scheduled', 'discard'],
    components: {
        SpinnerLoading
    }
});

export interface ScheduleTherapies {
    success: Therapy[],
    failed: boolean,
}
</script>